import type { ReactNode, WheelEvent } from "react";

import { cn } from "@/lib/utils";

interface ScrollableTextCellProps {
  children: ReactNode;
  className?: string;
  /** Horizontal scroll for single-line values (e.g. Trace ID). */
  axis?: "x" | "y";
}

const handleHorizontalWheel = (event: WheelEvent<HTMLDivElement>) => {
  const element = event.currentTarget;
  if (element.scrollWidth <= element.clientWidth) {
    return;
  }

  const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
  if (delta === 0) {
    return;
  }

  const atStart = element.scrollLeft <= 0;
  const atEnd = element.scrollLeft + element.clientWidth >= element.scrollWidth - 1;
  const scrollingLeft = delta < 0;
  const scrollingRight = delta > 0;

  if ((scrollingLeft && atStart) || (scrollingRight && atEnd)) {
    return;
  }

  element.scrollLeft += delta;
  event.preventDefault();
  event.stopPropagation();
};

const handleVerticalWheel = (event: WheelEvent<HTMLDivElement>) => {
  const element = event.currentTarget;
  const { scrollTop, scrollHeight, clientHeight } = element;
  if (scrollHeight <= clientHeight) {
    return;
  }

  const delta = event.deltaY;
  const atTop = scrollTop <= 0;
  const atBottom = scrollTop + clientHeight >= scrollHeight - 1;
  const scrollingUp = delta < 0;
  const scrollingDown = delta > 0;

  if ((scrollingUp && !atTop) || (scrollingDown && !atBottom)) {
    event.stopPropagation();
  }
};

export const ScrollableTextCell = ({
  children,
  className,
  axis = "x",
}: ScrollableTextCellProps) => (
  <div
    className={cn(
      "scrollbar-hide w-full min-w-0",
      axis === "x"
        ? "overflow-x-auto whitespace-nowrap"
        : "max-h-[60px] overflow-y-auto whitespace-normal break-words",
      className,
    )}
    onWheel={axis === "x" ? handleHorizontalWheel : handleVerticalWheel}
  >
    {children}
  </div>
);
